import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { Reveal } from "../Reveal";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const QUERY = process.env.REACT_APP_GOOGLE_PLACE_QUERY || "Jennie-Marie Female Lifestyle Consultant";

let mapsPromise = null;
function loadMaps() {
  if (window.google?.maps) return Promise.resolve();
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places&v=weekly`;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("maps-load-failed"));
    document.head.appendChild(s);
  });
  return mapsPromise;
}

const reviewText = (r) => (typeof r.text === "string" ? r.text : r.text?.text || "");
const reviewName = (r) => r.authorAttribution?.displayName || "Google user";
const reviewPhoto = (r) => r.authorAttribution?.photoURI || null;

export default function GoogleReviews() {
  const [place, setPlace] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!KEY) return setFailed(true);
    let active = true;
    (async () => {
      try {
        await loadMaps();
        const { Place } = await window.google.maps.importLibrary("places");
        const { places } = await Place.searchByText({
          textQuery: QUERY,
          fields: ["id", "displayName", "rating", "userRatingCount", "reviews", "googleMapsURI"],
        });
        if (!active) return;
        if (!places?.length) return setFailed(true);
        const p = places[0];
        setPlace({
          name: p.displayName,
          rating: p.rating,
          count: p.userRatingCount,
          uri: p.googleMapsURI,
        });
        setReviews((p.reviews || []).filter((r) => reviewText(r)).slice(0, 6));
      } catch (e) {
        if (active) setFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Hidden until real reviews are available (e.g. once the API key domain is whitelisted)
  if (failed || !place || reviews.length === 0) return null;

  return (
    <section id="google-reviews" data-testid="google-reviews-section" className="relative py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-gold">Reviews from Google</span>
              <div className="mt-4 flex items-center gap-4">
                <span className="font-serif text-5xl text-plum leading-none">{place.rating?.toFixed(1)}</span>
                <div>
                  <div className="flex gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} size={16} className={i < Math.round(place.rating) ? "fill-gold text-gold" : "text-plum/20"} />
                    ))}
                  </div>
                  <p className="mt-1 text-[12px] tracking-[0.1em] text-stone">Based on {place.count} Google reviews</p>
                </div>
              </div>
            </div>
            {place.uri && (
              <a
                data-testid="google-reviews-link"
                href={place.uri}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-plum border-b border-plum/30 pb-1 hover:border-plum transition-colors w-fit"
              >
                Read all on Google <ExternalLink size={14} />
              </a>
            )}
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              data-testid={`google-review-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              className="rounded-[6px] border border-border bg-cream p-7 flex flex-col"
            >
              <div className="flex gap-0.5 mb-4">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={14} className={s < Math.round(r.rating || 5) ? "fill-gold text-gold" : "text-plum/20"} />
                ))}
              </div>
              <p className="text-[15px] text-stone leading-relaxed flex-1 line-clamp-6">{reviewText(r)}</p>
              <div className="mt-6 flex items-center gap-3">
                {reviewPhoto(r) ? (
                  <img src={reviewPhoto(r)} alt={reviewName(r)} className="h-9 w-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="h-9 w-9 rounded-full bg-mne-pink flex items-center justify-center font-serif text-plum">
                    {reviewName(r).charAt(0)}
                  </span>
                )}
                <div>
                  <p className="text-[13px] uppercase tracking-[0.1em] text-plum">{reviewName(r)}</p>
                  <p className="text-[11px] text-stone">{r.relativePublishTimeDescription || "Google review"}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
